// Simple monitoring and logging module
// import LogRocket from "logrocket";

// Define LogRocket type interface
interface LogRocketInterface {
  init(appId: string): void;
  identify(uid: string, traits?: Record<string, any>): void;
  track(name: string, properties?: Record<string, any>): void;
  captureException(error: Error, options?: Record<string, any>): void;
  // Add other LogRocket methods as needed
}

let logRocketInstance: LogRocketInterface | null = null;

const getLogRocket = (): LogRocketInterface => {
  if (!logRocketInstance) {
    // logRocketInstance = LogRocket as LogRocketInterface;
    // Mock LogRocket for development
    logRocketInstance = {
      init: () => {},
      identify: () => {},
      track: () => {},
      captureException: () => {}
    } as LogRocketInterface;
  }
  return logRocketInstance;
};

const isDev = import.meta.env.MODE === "development";

/**
 * Log information messages
 * In development: console.log
 * In production: This would be connected to a proper logging service
 */
export const logInfo = (message: string, data?: any): void => {
  if (isDev) {
    console.log(`INFO: ${message}`, data ? data : "");
  } else {
    // In production, this would send to a logging service
    // Example: LogService.info(message, data);

    // For now, we'll still use console.log but with limited data
    console.log(`INFO: ${message}`);
  }
};

/**
 * Log error messages
 * In development: console.error with full details
 * In production: Structured error logging with appropriate filtering of sensitive data
 */
export const logError = (message: string, data?: any): void => {
  if (isDev) {
    console.error(`ERROR: ${message}`, data ? data : "");
  } else {
    // Filter sensitive data before logging in production
    let sanitizedData = {};

    if (data) {
      // Clone the data to avoid modifying the original
      sanitizedData = { ...data };

      // Remove potential sensitive information
      if (sanitizedData.headers) delete sanitizedData.headers.Authorization;
      if (sanitizedData.password) sanitizedData.password = "[REDACTED]";
      if (sanitizedData.token) sanitizedData.token = "[REDACTED]";

      // Limit error message details in production
      if (sanitizedData.error && sanitizedData.error.stack) {
        sanitizedData.error = {
          name: sanitizedData.error.name,
          message: sanitizedData.error.message,
        };
      }
    }

    // In production, this would send to error monitoring service
    // Example: ErrorMonitoring.captureException(message, sanitizedData);

    // For now we'll use console.error with sanitized data
    console.error(`ERROR: ${message}`, sanitizedData);
  }
};

/**
 * Track user actions for analytics
 */
export const trackEvent = (
  eventName: string,
  properties?: Record<string, any>
): void => {
  // Always log in development
  if (isDev) {
    console.log(`📊 EVENT: ${eventName}`, properties ? properties : "");
  }

  // Always try to track with LogRocket if available
  try {
    const logRocket = getLogRocket();
    logRocket.track(eventName, properties);
    if (isDev) {
      console.log(`✅ LogRocket tracked: ${eventName}`);
    }
  } catch (err) {
    console.warn("LogRocket not available for event tracking", err);
  }

  // In production, this would also send to analytics service
  // Example: Analytics.track(eventName, properties);
};

/**
 * Track page views for analytics
 */
export const trackPageView = (
  pageName: string,
  properties?: Record<string, any>
): void => {
  if (isDev) {
    console.log(`PAGE VIEW: ${pageName}`, properties ? properties : "");
  } else {
    // Track with LogRocket if available
    try {
      const logRocket = getLogRocket();
      logRocket.track(`Page View: ${pageName}`, properties);
    } catch (err) {
      console.warn("LogRocket not available for page tracking", err);
    }
    // In production, this would also send to analytics service
    // Example: Analytics.pageView(pageName, properties);
  }
};

/**
 * Identify user for LogRocket session tracking
 */
export const identifyUser = (
  userId: string,
  userInfo?: Record<string, any>
): void => {
  // Always log in development
  if (isDev) {
    console.log(`👤 IDENTIFY USER: ${userId}`, userInfo ? userInfo : "");
  }

  // Always try to identify with LogRocket if available
  try {
    const logRocket = getLogRocket();
    logRocket.identify(userId, userInfo);
    console.log(`✅ User identified in LogRocket: ${userId}`, {
      mode: import.meta.env.MODE,
      userInfo: userInfo ? Object.keys(userInfo) : "none",
    });
  } catch (err) {
    console.warn("LogRocket not available for user identification", err);
  }
};

/**
 * Capture exceptions with LogRocket
 */
export const captureException = (
  error: Error,
  context?: Record<string, any>
): void => {
  logError(error.message, { error, context });

  if (!isDev) {
    try {
      const logRocket = getLogRocket();
      logRocket.captureException(error, context);
    } catch (err) {
      console.warn("LogRocket not available for exception capture", err);
    }
  }
};

/**
 * Initialize monitoring services
 */
export const initMonitoring = (): void => {
  const projectId = import.meta.env.VITE_LOGROCKET_PROJECT_ID;
  const mode = import.meta.env.MODE;

  console.log("🚀 Initializing monitoring services", {
    mode,
    isDev,
    projectId: projectId ? `${projectId.substring(0, 10)}...` : "NOT SET",
  });

  // Skip LogRocket in development to avoid errors
  if (isDev) {
    console.log("⚠️ Skipping LogRocket initialization in development mode");
    return;
  }

  // Initialize LogRocket when project ID is provided (prod only)
  if (projectId) {
    try {
      const logRocket = getLogRocket();
      logRocket.init(projectId);
      console.log("✅ LogRocket initialized successfully", {
        projectId: `${projectId.substring(0, 10)}...`,
        mode,
      });

      // Add a test event to verify it's working
      logRocket.track("LogRocket Initialized", {
        mode,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent.substring(0, 50),
      });
    } catch (err) {
      console.error("❌ Failed to initialize LogRocket", err);
    }
  } else {
    console.warn(
      "⚠️ LogRocket project ID not found. Set VITE_LOGROCKET_PROJECT_ID environment variable."
    );
  }

  // Initialize error tracking
  // Example: ErrorMonitoring.init({ dsn: 'https://...', environment: import.meta.env.MODE });

  // Initialize analytics
  // Example: Analytics.init({ apiKey: '...' });
};
